export const supportedLocales = ["en", "ru", "es", "de", "fr", "it", "pt-PT"] as const;
export type Locale = (typeof supportedLocales)[number];
export type LocalePreference = "auto" | Locale;
export type PreviewLocale = Locale | "en-XA";
export type LocaleEnvironment = "production" | "staging" | "development";

export const localeNames: Readonly<Record<Locale, string>> = {
  en: "English",
  ru: "Русский",
  es: "Español",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
  "pt-PT": "Português",
};

// Launch all seven complete catalogs together. Exposure remains separate from
// storage validation: rollback must not erase an already-saved preference.
const productionLocales: readonly Locale[] = supportedLocales;
const previewLocales: readonly Locale[] = supportedLocales;
export function enabledLocales(environment: LocaleEnvironment): readonly Locale[] {
  return environment === "production" ? productionLocales : previewLocales;
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && supportedLocales.includes(value as Locale);
}

export function isLocalePreference(value: unknown): value is LocalePreference {
  return value === "auto" || isLocale(value);
}

/** Preserve valid observed languages, even those we have not translated yet. */
export function normalizeLanguageTag(value: unknown): string | undefined {
  if (typeof value !== "string" || !value.trim() || value.length > 64) return undefined;
  try {
    const [tag] = Intl.getCanonicalLocales(value.trim());
    // Pseudo-locales are never account preferences, telemetry hints, or bot locales.
    return tag === "en-XA" ? undefined : tag;
  } catch {
    return undefined;
  }
}

/** Normalize a language tag, not an Accept-Language header. Unknown is absent. */
export function normalizeLocale(value: unknown): Locale | undefined {
  const language = normalizeLanguageTag(value)?.split("-")[0];
  if (language === "pt") return "pt-PT";
  return isLocale(language) ? language : undefined;
}

export function detectBrowserLocale(languages: readonly string[]): Locale {
  for (const language of languages) {
    const locale = normalizeLocale(language);
    if (locale) return locale;
  }
  return "en";
}

export function resolveWebLocale(preference: unknown, languages: readonly string[]): Locale {
  return isLocale(preference) ? preference : detectBrowserLocale(languages);
}

export function resolveTelegramLocale(preference: unknown, telegramLanguage?: unknown): Locale {
  return isLocale(preference) ? preference : normalizeLocale(telegramLanguage) ?? "en";
}

export function resolveEmailLocale(input: {
  preference?: unknown;
  requestLocale?: unknown;
  lastWebLocale?: unknown;
  telegramLanguage?: unknown;
}): Locale {
  return isLocale(input.preference) ? input.preference :
    normalizeLocale(input.requestLocale) ?? normalizeLocale(input.lastWebLocale) ??
    normalizeLocale(input.telegramLanguage) ?? "en";
}

export function exposedLocale(locale: Locale, environment: LocaleEnvironment): Locale {
  return enabledLocales(environment).includes(locale) ? locale : "en";
}

export function telegramLanguageCode(locale: Locale): string {
  return locale === "pt-PT" ? "pt" : locale;
}
